import { Card, Flex, Skeleton } from '@radix-ui/themes';
import { CanvasHelper } from 'classes/helpers/canvas.helper';
import { PlateCanvas } from 'classes/plate-canvas';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { PlotHeader } from 'components/sections/admin-portal/machines/dialogs/training-performance/plots/plot-header';
import useTrainingPerformanceStore from 'components/sections/admin-portal/machines/dialogs/training-performance/store/use-training-performance-store';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { MAX_PLATE_DELTA_IN } from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { WithID } from 'lib_ts/interfaces/common/i-generics';
import { IPlateLoc } from 'lib_ts/interfaces/pitches';
import { IAziAltCoordinate } from 'lib_ts/interfaces/pitches/i-base';
import { IMachineShot } from 'lib_ts/interfaces/training/i-machine-shot';
import { IItrLogExt } from 'lib_ts/interfaces/training/i-training-log';
import { first, mean, sum } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import useMiscStore from 'stores/use-misc-store';

const REDRAW_DEQUEUE_MS = 100;

const SKIP_CENTER_TARGETS = true;

const TARGET_SIZE_PX = 15;

enum PlotMode {
  Actual = 'Actual',
  MeanCentered = 'Mean Centered',
  TargetCentered = 'Target Centered',
  TargetCenteredAvg = 'Target Centered (Avg)',
}

// if plotting at most this many shots, draw each shot's target + line + label automatically
const ACTUAL_TARGETS_SHOTS_THRESHOLD = 1;

const getShotPlateError = (shot: IMachineShot) => {
  if (!shot.traj) {
    return undefined;
  }

  if (!shot.target_traj) {
    return undefined;
  }

  const actual = TrajHelper.getPlateLoc(shot.traj);
  const target = shot.plate ?? TrajHelper.getPlateLoc(shot.target_traj);

  const output: IPlateLoc = {
    plate_x: actual.plate_x - target.plate_x,
    plate_z: actual.plate_z - target.plate_z,
  };

  return output;
};

const getAvgPlateError = (shots: IMachineShot[]) => {
  const errors: IPlateLoc[] = [];

  shots.forEach((shot) => {
    const shotError = getShotPlateError(shot);

    if (shotError) {
      errors.push(shotError);
    }
  });

  if (errors.length === 0) {
    return undefined;
  }

  const output: IPlateLoc = {
    plate_x: mean(errors.map((m) => m.plate_x)),
    plate_z: mean(errors.map((m) => m.plate_z)),
  };

  return output;
};

export const TrainingPerfPlotPlate = (props: { iterations: IItrLogExt[] }) => {
  const loading = useTrainingPerformanceStore((s) => s.loading);
  const showThresholds = useTrainingPerformanceStore((s) => s.showThresholds);

  const logShots = useTrainingPerformanceStore((s) => s.logShots);
  const selectedShotIds = useTrainingPerformanceStore((s) => s.selectedShotIds);

  const [mode, setMode] = useState<PlotMode>(PlotMode.MeanCentered);

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const canvasDef = useMemo(
    () => PlateCanvas.makeAbstractSquare(isDarkMode),
    [isDarkMode]
  );

  // what is used for assembly of the results
  const mergeCanvas = useRef<HTMLCanvasElement>(null);
  const mainCanvas = useRef<HTMLCanvasElement>(null);
  const shotsCanvas = useRef<HTMLCanvasElement>(null);

  // what gets seen by users + LogRocket
  const [imgSrc, setImgSrc] = useState<string>();
  // for dequeuing updates to the imgSrc
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const updateTimeout = useRef<NodeJS.Timeout>();

  // aggregate updates from various canvases before updating imgSrc
  useEffect(() => {
    updateTimeout.current = setTimeout(async () => {
      const canvas = mergeCanvas.current;

      if (!canvas) {
        return;
      }

      // all the canvases that need to be combined should go here
      const sources = [mainCanvas.current, shotsCanvas.current].filter(
        (c) => c
      ) as HTMLCanvasElement[];

      const src = await CanvasHelper.combineToPNG({
        sources: sources,
        target: canvas,
      });

      setImgSrc(src);
    }, REDRAW_DEQUEUE_MS);

    return () => clearTimeout(updateTimeout.current);
  }, [lastUpdate]);

  // drawMain
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = mainCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        canvasDef.drawPitchReportRulers(ctx, 10);
        canvasDef.drawStrikeZone(ctx, true);
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [loading]);

  // drawShots
  useEffect(() => {
    const callback = async () => {
      try {
        const canvas = shotsCanvas.current;
        if (!canvas) {
          return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const centerPlate: IPlateLoc = {
          plate_x: canvasDef.CONFIG.x.defaultFt,
          plate_z: canvasDef.CONFIG.y.defaultFt,
        };

        // ? the errors are relative to (0,0) but the plate canvas renders (0,2.5) as the origin
        const errorToPlate = (error: IPlateLoc) => {
          const o: IPlateLoc = {
            plate_x: error.plate_x + centerPlate.plate_x,
            plate_z: error.plate_z + centerPlate.plate_z,
          };

          return o;
        };

        const drawCenteredTarget = async () => {
          if (SKIP_CENTER_TARGETS) {
            return;
          }

          await canvasDef.drawTarget(ctx, {
            as: 'crosshairs',
            target: centerPlate,
            color: `rgba(150,150,150,100%)`,
            width_px: TARGET_SIZE_PX,
          });

          return true;
        };

        const drawCenteredThreshold = () =>
          canvasDef.drawThreshold(ctx, {
            target: centerPlate,
            radiusIn: MAX_PLATE_DELTA_IN,
            fillColor: `rgba(255,255,255,20%)`,
            borderColor: `rgba(255,255,255,75%)`,
          });

        switch (mode) {
          case PlotMode.Actual: {
            // * only counts shots w/ traj defined, accounting for checked shots too
            const totalShots =
              sum(
                props.iterations.map(
                  (g) =>
                    logShots
                      .filter((s) => g.shotIDs.includes(s._id))
                      .filter((s) => s.traj).length
                )
              ) ?? 0;

            // ? only draw targets, labels, and lines for shots if drawing a "small" number of shots
            const showTargets = totalShots <= ACTUAL_TARGETS_SHOTS_THRESHOLD;

            if (showThresholds) {
              // draw acceptable bubble around target
              drawCenteredThreshold();
            }

            for (const itr of props.iterations) {
              // * use original shots, not rotated
              const shots = logShots.filter((s) => itr.shotIDs.includes(s._id));

              for (const shot of shots) {
                if (!shot.traj) {
                  // skip the shot if no traj
                  continue;
                }

                if (!shot.target_traj) {
                  // skip the shot if no target traj
                  continue;
                }

                // while something is selected but not this shot, render it in a more subdued styling
                const isSecondary =
                  selectedShotIds.length > 0 &&
                  !selectedShotIds.includes(shot._id);

                const style = CanvasHelper.getDotStyle(itr.rgb, isSecondary);

                // draw shot
                const targetPlate = centerPlate;
                const shotPlate = TrajHelper.getPlateLoc(shot.traj);

                canvasDef.drawActual(ctx, {
                  value: shotPlate,
                  size: 3,
                  fillColor: `rgba(${style.rgb},${style.fillOpacity})`,
                  borderColor: `rgba(${style.rgb},${style.lineOpacity})`,
                });

                // draw line and label from shot to target
                if (showTargets && !isSecondary) {
                  // await canvasDef.drawTarget(ctx, {
                  //   as: 'crosshairs',
                  //   target: targetPlate,
                  //   color: `rgba(${style.color},${style.lineOpacity})`,
                  //   width_px: TARGET_SIZE_PX,
                  // });

                  canvasDef.drawLine(ctx, {
                    from: shotPlate,
                    to: targetPlate,
                    lineColor: `rgba(${style.rgb},${style.lineOpacity})`,
                  });

                  canvasDef.drawLabel(ctx, {
                    value: shotPlate,
                    target: targetPlate,
                    fontColor: `rgba(${style.rgb},${style.lineOpacity})`,
                  });
                }
              }
            }
            break;
          }

          case PlotMode.TargetCentered: {
            if (showThresholds) {
              drawCenteredThreshold();
            }

            // todo: will we ever label shots?
            const labelShots = false;

            for (const itr of props.iterations) {
              // if the user has selected specific shots, only draw those
              const shots = logShots.filter((s) => itr.shotIDs.includes(s._id));

              for (const shot of shots) {
                const shotError = getShotPlateError(shot);

                if (!shotError) {
                  continue;
                }

                // while something is selected but not this shot, render it in a more subdued styling
                const isSecondary =
                  selectedShotIds.length > 0 &&
                  !selectedShotIds.includes(shot._id);

                const style = CanvasHelper.getDotStyle(itr.rgb, isSecondary);

                const shotErrorPlate = errorToPlate(shotError);

                // draw line and label from error to center
                if (labelShots && !isSecondary) {
                  canvasDef.drawLine(ctx, {
                    from: shotErrorPlate,
                    to: centerPlate,
                    lineColor: `rgba(${style.rgb},${style.lineOpacity})`,
                  });

                  canvasDef.drawLabel(ctx, {
                    value: shotErrorPlate,
                    target: centerPlate,
                    fontColor: `rgba(${style.rgb},${style.lineOpacity})`,
                  });
                }

                // draw shot error
                canvasDef.drawActual(ctx, {
                  value: shotErrorPlate,
                  size: 3,
                  fillColor: `rgba(${style.rgb},${style.fillOpacity})`,
                  borderColor: `rgba(${style.rgb},${style.lineOpacity})`,
                });
              }
            }

            await drawCenteredTarget();
            break;
          }

          case PlotMode.TargetCenteredAvg: {
            if (showThresholds) {
              drawCenteredThreshold();
            }

            // todo: will we ever label shots?
            const labelShots = true;

            for (const itr of props.iterations) {
              // if the user has selected specific shots, only draw those
              const shots = logShots.filter((s) => itr.shotIDs.includes(s._id));

              const avgError = getAvgPlateError(shots);

              if (!avgError) {
                continue;
              }

              const avgErrorPlate = errorToPlate(avgError);

              // draw line and label from avg error to center
              if (labelShots) {
                // no need for lines since they all converge to the origin
                // canvasDef.drawLine(ctx, {
                //   from: avgErrorPlate,
                //   to: centerPlate,
                //   lineColor: `rgba(${pitch.rgb},75%)`,
                // });

                canvasDef.drawLabel(ctx, {
                  value: avgErrorPlate,
                  target: centerPlate,
                  fontColor: `rgba(${itr.rgb},100%)`,
                });
              }

              // draw avg error
              canvasDef.drawActual(ctx, {
                value: avgErrorPlate,
                size: 3,
                fillColor: `rgba(${itr.rgb},75%)`,
                borderColor: `rgba(${itr.rgb},100%)`,
              });
            }

            await drawCenteredTarget();
            break;
          }

          case PlotMode.MeanCentered: {
            // Computes the mean plate location (x, z) across all shots that have a traj.
            const getAvgPlate = (shots: IMachineShot[]) => {
              const plates: IPlateLoc[] = [];

              shots.forEach((s) => {
                if (!s.traj) {
                  return;
                }

                plates.push(TrajHelper.getPlateLoc(s.traj));
              });

              if (plates.length === 0) {
                return undefined;
              }

              const output: IPlateLoc = {
                plate_x: mean(plates.map((p) => p.plate_x)),
                plate_z: mean(plates.map((p) => p.plate_z)),
              };

              return output;
            };

            if (showThresholds) {
              drawCenteredThreshold();
            }

            // --- Primary pitch groups ---
            for (const itr of props.iterations) {
              // todo: will we ever label shots?
              const labelShots = false;

              // if the user has selected specific shots, only draw those
              const shots = logShots.filter((s) => itr.shotIDs.includes(s._id));

              // Compute the mean plate location for this pitch group.
              const avgPlate = getAvgPlate(shots);

              if (!avgPlate) {
                continue;
              }

              // All rotated shots share the same py (distance to the plate), so take it from the first.
              const refPy = first(shots)?.traj?.py; // rotated shots will all have same py

              if (!refPy) {
                continue;
              }

              // Compute the angular correction (altitude = vertical, azimuth = horizontal) that would
              // bring the mean plate location to the center of the strike zone.
              // The negative sign converts "displacement toward center" into a rotation delta.
              const avgDelta: IAziAltCoordinate = {
                altitude_rad: -Math.atan(
                  (avgPlate.plate_z - centerPlate.plate_z) / refPy
                ),
                azimuth_rad: -Math.atan(
                  (avgPlate.plate_x - centerPlate.plate_x) / refPy
                ),
              };

              // Apply the mean-centering rotation to every shot's trajectory and collect
              // the resulting plate locations.
              const rotPlates: WithID<IPlateLoc>[] = [];

              for (const shot of shots) {
                if (!shot.traj) {
                  continue;
                }

                const rotTraj = TrajHelper.rotateTraj(shot.traj, avgDelta);

                // apply avg tilt and yaw deltas to shot traj
                const rotPlate = TrajHelper.getPlateLoc(rotTraj);
                rotPlates.push({
                  ...rotPlate,
                  _id: shot._id,
                });
              }

              // After rotation the mean of the rotated plates should be very close to centerPlate,
              // but a small residual can remain due to the non-linearity of atan.
              // Compute that residual so we can subtract it out and guarantee a perfectly centered mean.
              const avgRotPlate: IPlateLoc = {
                plate_x: mean(rotPlates.map((p) => p.plate_x)),
                plate_z: mean(rotPlates.map((p) => p.plate_z)),
              };

              // How far the rotated mean still is from the canvas center.
              const avgRotOffset: IPlateLoc = {
                plate_x: avgRotPlate.plate_x - centerPlate.plate_x,
                plate_z: avgRotPlate.plate_z - centerPlate.plate_z,
              };

              // Draw each shot shifted by the residual offset so the cloud is exactly centered.
              for (const rotPlate of rotPlates) {
                // while something is selected but not this shot, render it in a more subdued styling
                const isSecondary =
                  selectedShotIds.length > 0 &&
                  !selectedShotIds.includes(rotPlate._id);

                const style = CanvasHelper.getDotStyle(itr.rgb, isSecondary);

                const shotPlate: IPlateLoc = {
                  plate_x: rotPlate.plate_x - avgRotOffset.plate_x,
                  plate_z: rotPlate.plate_z - avgRotOffset.plate_z,
                };

                // draw line and label from shot to center
                if (labelShots && !isSecondary) {
                  canvasDef.drawLine(ctx, {
                    from: shotPlate,
                    to: centerPlate,
                    lineColor: `rgba(${style.rgb},${style.lineOpacity})`,
                  });

                  canvasDef.drawLabel(ctx, {
                    value: shotPlate,
                    target: centerPlate,
                    fontColor: `rgba(${style.rgb},${style.lineOpacity})`,
                  });
                }

                // draw shot
                canvasDef.drawActual(ctx, {
                  value: shotPlate,
                  size: 3,
                  fillColor: `rgba(${style.rgb},${style.fillOpacity})`,
                  borderColor: `rgba(${style.rgb},${style.lineOpacity})`,
                });
              }
            }

            // Draw the centered crosshairs (and optional threshold circle) at the strike-zone origin.
            await drawCenteredTarget();
            break;
          }

          default: {
            break;
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLastUpdate(Date.now());
      }
    };

    callback();
  }, [props.iterations, mode, showThresholds, logShots, selectedShotIds]);

  return (
    <ErrorBoundary identifier="TrainingPerfPlotPlate">
      <Skeleton loading={loading}>
        <Flex direction="column" gap="3">
          <PlotHeader
            title="Plate Location"
            subtitle="Plot of plate locations from catcher's view"
            children={
              <CommonSelectInput
                id="plate-plot-mode"
                variant="soft"
                inputColor={RADIX.COLOR.NEUTRAL}
                size="1"
                value={mode}
                onChange={(v) => setMode(v as PlotMode)}
                options={[
                  {
                    label: 'Mean-centered',
                    value: PlotMode.MeanCentered,
                    description: 'Visualize the spray of the pitch',
                  },
                  {
                    label: 'Target-centered',
                    value: PlotMode.TargetCentered,
                    description: 'Visualize the plate error',
                  },
                  {
                    label: 'Target-centered (Averaged)',
                    value: PlotMode.TargetCenteredAvg,
                    description: 'Visualize the average plate error',
                  },
                  {
                    label: 'Actual',
                    value: PlotMode.Actual,
                    description: 'Visualize the true plate location',
                  },
                ]}
                skipSort
              />
            }
          />

          <Card className="no-padding">
            <div className="slider">
              <div
                className="sizer"
                style={{
                  aspectRatio: canvasDef.CONFIG.canvas.aspectRatio,
                }}
              >
                {/* for log rocket recordings to show the results */}
                {imgSrc && (
                  <img
                    alt="plate performance plot"
                    className="image-wrapper"
                    src={imgSrc}
                  />
                )}
                <canvas
                  ref={mergeCanvas}
                  data-identifier="merge-canvas"
                  width={canvasDef.CONFIG.canvas.widthPx}
                  height={canvasDef.CONFIG.canvas.heightPx}
                />
                <canvas
                  ref={mainCanvas}
                  data-identifier="main-canvas"
                  width={canvasDef.CONFIG.canvas.widthPx}
                  height={canvasDef.CONFIG.canvas.heightPx}
                />
                <canvas
                  ref={shotsCanvas}
                  data-identifier="shots-canvas"
                  width={canvasDef.CONFIG.canvas.widthPx}
                  height={canvasDef.CONFIG.canvas.heightPx}
                />
              </div>
            </div>
          </Card>
        </Flex>
      </Skeleton>
    </ErrorBoundary>
  );
};
